import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';

import { PolygonManaged, PolygonInfo, CoordenateInfo} from './interfaces';

import {DrawManager } from './DrawManager';

class PolygonsManager 
{
    public version: string ='PolygonsManager.v202607061629';

    public isLoadingPolygons: boolean = false;

    private polygons = new Map<string, PolygonManaged>();
    public map: google.maps.Map | null = null;    

    public drawManager:DrawManager| null=null;


    constructor() 
    {
    }

    public SetMap(map: google.maps.Map)
    {
        this.map = map;
        // DrawManager se crea de forma perezosa en EnabledDrawingAsync (solo cuando se va a dibujar).
        // Evita instanciar el DrawingManager deprecado (v3.65) en vistas de solo lectura.
    }

    public async AddPolygonAsync( polygonInfo:PolygonInfo, polygon?:google.maps.Polygon ): Promise<void>    
    {
        // await this.isReady();

        if (!this.map) 
        {
            console.error('Map not initialized');
            return;
        }

        const polygonOptions: google.maps.PolygonOptions = 
        {
            paths: polygonInfo.paths,
            strokeColor: polygonInfo.strokeColor || '#FF0000',
            strokeOpacity: parseFloat(polygonInfo.strokeOpacity) || 0.8,
            strokeWeight: parseInt(polygonInfo.strokeWeight) || 2,
            fillColor: polygonInfo.fillColor || '#FF0000',
            fillOpacity: parseFloat(polygonInfo.fillOpacity) || 0.35,
            map: this.map,
            editable: false, 
            draggable: false,            
        };

        if(!polygon) polygon = new google.maps.Polygon(polygonOptions);
        
        polygon.setVisible(polygonInfo.visible || false);
        
        let polygonManaged:PolygonManaged=
        {
            id: polygonInfo.id, 
            polygon: polygon           
        }

        this.polygons.set(polygonInfo.id, polygonManaged);
        
        if(!polygonInfo) return;

        //calcula el centro
        if(!polygonInfo.centro?.label) return;
        let center:google.maps.LatLng|null=null;
        if(!polygonInfo.centro || !polygonInfo.centro.position ||  polygonInfo.centro.position.lat===0 || polygonInfo.centro.position.lng===0)
        {
            const bounds = new google.maps.LatLngBounds();
           //polygon.getPaths().forEach(p => bounds.extend(p));
            polygon.getPaths().forEach((path) => { path.forEach((latLng) => bounds.extend(latLng));});
            center = bounds.getCenter(); // sin 'let': asigna la variable externa (antes se sombreaba y quedaba null)
        }
        else
        {
            center=new google.maps.LatLng(polygonInfo.centro.position.lat, polygonInfo.centro.position.lng);   
        }
 
        const labelMarker = new google.maps.marker.AdvancedMarkerElement({
            map: this.isLoadingPolygons ? null : this.map,
            position:center,//polygonInfo.centro?.position,
            content: this.CreateCustomIcon( polygonInfo.centro?.icon|| 'default-icon.png', polygonInfo.centro?.label || '' ),
            title: polygonInfo.centro?.label,
            gmpDraggable: false,
            gmpClickable: false
        });

        // Guardar la etiqueta junto al polígono para poder retirarla luego (evita etiquetas huérfanas al redibujar).
        polygonManaged.labelMarker = labelMarker;
    }

    public ShowAllPolygons(): void
    {
        this.polygons.forEach((polygonManaged) => {
            if (polygonManaged.polygon) {
                polygonManaged.polygon.setMap(this.map);
            }
        });
    }

    public UpdatePolygon(polygonInfo:PolygonInfo):void
    {
        let polygon  = this.polygons.get(polygonInfo.id);

        if (polygon == null) return;  
        
        polygon.polygon.setVisible(polygonInfo.visible || false);
    }

    public GetPolygonInfo(id: string): PolygonInfo | undefined
    {
        if(!this.polygons) return undefined;

        let poligonManaged = this.polygons.get(id);

        if (!poligonManaged) return undefined;

        const paths: CoordenateInfo[] = [];
        poligonManaged.polygon.getPaths().forEach((path) => { 
            path.forEach((latLng) =>
            { 
                paths.push( { lat: latLng.lat(), lng: latLng.lng() });
            });
        });

        return {
            id: poligonManaged.id,
            paths: paths,
            strokeColor: '#FF0000',
            strokeOpacity:  '0.8',
            strokeWeight:'2',
            fillColor:  '#FF0000',
            fillOpacity:  '0.35',
            centro: null,
            visible: poligonManaged.polygon.getVisible() || false
        };
    }

    public RemovePolygon(idPolygon:string):void
    {
        let polygonManaged= this.polygons.get(idPolygon);

        if (!polygonManaged) return;

        polygonManaged.polygon.setMap(null);
        if (polygonManaged.labelMarker) polygonManaged.labelMarker.map = null; // retirar también la etiqueta
        this.polygons.delete(idPolygon);
    }

    private CreateCustomIcon(iconUrl: string, label?: string): HTMLElement 
    {
        const container = document.createElement('div');
        container.style.position = 'relative';
        container.style.display = 'inline-block';
        
        const markerImage = document.createElement('img');
        markerImage.src = iconUrl; 
        markerImage.style.width = '32px';
        markerImage.style.height = '32px';
        
        container.appendChild(markerImage);
        
        if (label) {
            const labelElement = document.createElement('div');
            labelElement.textContent = label;
            labelElement.style.position = 'absolute';
            labelElement.style.top = '-5px';
            labelElement.style.left = '50%';
            labelElement.style.transform = 'translateX(-50%)';
            labelElement.style.backgroundColor = 'transparent';
            labelElement.style.padding = '2px 6px';
            labelElement.style.borderRadius = '3px';
            labelElement.style.fontSize = '13px';
            labelElement.style.fontWeight = 'bold';
            labelElement.style.whiteSpace = 'nowrap';
            labelElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
            
            container.appendChild(labelElement);
        }
        
        return container;        
    }

    public async EnabledDrawingAsync(poligonInfo: PolygonInfo):Promise<void>
    {
        if(!this.map) return;
        if(!this.drawManager) this.drawManager = new DrawManager( this.map );

        poligonInfo.visible=true;

        let poygon =  this.polygons.get( poligonInfo.id );

        if( poygon ) this.RemovePolygon( poligonInfo.id );

        this.drawManager.enableDrawing(async (polygon: google.maps.Polygon)=>
        {
            await this.AddPolygonAsync(poligonInfo, polygon);
        });
    }

    public async DisposeAsync(): Promise<void>
    {
        try 
        {
            if(this.polygons)
            {
                this.polygons.forEach((polygon) => {
                    if(polygon.polygon)
                    {
                        google.maps.event.clearInstanceListeners(polygon.polygon);
                        polygon.polygon.setMap(null);
                    }
                    if(polygon.labelMarker) polygon.labelMarker.map = null; // retirar también la etiqueta
                });
                this.polygons.clear();
            }

            if(!this.map) return;
            this.map=null;
        } 
        catch (error) 
        {
            console.error('Error disposing map:', error);
            throw error;
        }
    }
}

export {PolygonsManager};


